import { MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import { makeStyles } from "tss-react/mui"
import { Box, Stack } from "@mui/system"
import { useMemo, useState } from "react"
import { useDateTime } from "../../../../localization/useDateTime"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { SearchOrdersInPopup } from "./SearchOrdersInPopup"
import { AnyMeeting, hasDeliveryDetails } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AnyMeetingServiceOrder } from "../../production/meetingService/components/OrderPackageSlip"
import { MeetingDetailsOrder } from "../../../../nonAdminUser/reports/home/meetingServiceApi"
import { tailwindPalette } from "../../../../theme/tailwindPalette"

const useStyles = makeStyles()((theme) => ({
    headerText: {
        fontSize: "1.125rem",
        fontWeight: 500,
        color: "black",
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    orderSelect: {
        minWidth: "12.5rem",
        width: "fit-content",
        fontSize: "1rem",
        fontWeight: 500,
        color: "black",
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "& .MuiSelect-select": {
            paddingRight: "1.5rem !important",
            paddingLeft: 0,
        },
        "& .MuiSelect-icon": {
            display: "none",
        },
    },
    selectDisplayContainer: {
        display: "flex",
        alignItems: "center",
        gap: ".25rem",
        width: "fit-content",
    },
    layoutBlankFill: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    selectedSlip: {
        fontWeight: 500,
    },
    headerDropdownMenuItem: {
        marginRight: 0,
        paddingRight: "16px",
        zIndex: 1,
    },
    orderSelectItem: {
        maxHeight: 350,
        borderRadius: "0.5rem",
        minWidth: "40rem !important",
        ".MuiMenuItem-root:hover": {
            backgroundColor: tailwindPalette.slate[50],
        },
        ".MuiMenu-list": {
            padding: 0,
        },
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    dropdownItemTitle: {
        fontWeight: 500,
    },
    menuItem: {
        padding: "0.75rem 1rem",
    },
}))

type OrdersType = AnyMeeting[] | AnyMeetingServiceOrder[]
type OrderType = AnyMeeting | AnyMeetingServiceOrder

function hasOrganizers(order: OrderType) {
    return "organizers" in order
}

function hasCustomer(order: OrderType) {
    return "customer" in order
}

const SelectDisplayReceiptComponent = ({ value, meetings }: { value: string; meetings: AnyMeeting[] }) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const meeting = meetings.find((m) => m.uid === value)

    return (
        <div className={classes.selectDisplayContainer}>
            <Typography className={classes.selectedSlip}>
                {meeting?.meetingDetails.meetingName || ""}, #{meeting?.id}
                {meeting &&
                    hasDeliveryDetails(meeting) &&
                    meeting?.deliveryDetails?.time &&
                    `, ${dateTimeFactory.formatDate(dateTimeFactory.fromApi(meeting?.deliveryDetails?.time))}`}
            </Typography>
            <KeyboardArrowDown fontSize="small" />
        </div>
    )
}

const SelectDisplayPosReceiptComponent = ({ value, orders }: { value: string; orders: AnyMeeting[] }) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const order = orders.find((m) => m.uid === value)
    const orderDate = order?.created
        ? dateTimeFactory.formatCustom(dateTimeFactory.fromApi(order?.created), "MMM d'th', yyyy")
        : ""

    return (
        <Box className={classes.selectDisplayContainer}>
            <LocalizedStrict
                id="receipt-order-label"
                vars={{
                    orderId: order?.id.toString() ?? "",
                    orderDate,
                }}
            >
                <Typography className={classes.headerText}>
                    Receipt #{order?.id.toString()}, {orderDate}
                </Typography>
            </LocalizedStrict>
            <KeyboardArrowDown fontSize="small" />
        </Box>
    )
}

const SelectDisplaySlipComponent = ({ value, orders }: { value: string; orders: AnyMeetingServiceOrder[] }) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const order = orders.find((m) => m.uid === value)

    return (
        <Box className={classes.selectDisplayContainer}>
            <Typography className={classes.selectedSlip}>
                {order?.meetingDetails.meetingName || ""}, #{order?.id},{" "}
                {dateTimeFactory.formatDate(dateTimeFactory.fromApi(order?.deliveryDetails.time ?? ""))}
            </Typography>
            <KeyboardArrowDown fontSize="small" />
        </Box>
    )
}

export const ReceiptDisplayDropdownMenu = ({ order }: { order: OrderType }) => {
    const dateTimeFactory = useDateTime()
    const { classes } = useStyles()

    const personName = useMemo(() => {
        if (hasOrganizers(order)) {
            return getOrderPerson(order.organizers).person?.name
        }
        if (hasCustomer(order)) {
            return order.customer.displayName
        }
        return ""
    }, [order])

    const idAndName = useMemo(() => {
        if (order?.meetingDetails?.meetingName) {
            return `#${order?.id} - ${order?.meetingDetails?.meetingName}`
        }
        return order?.id
    }, [order])

    const deliveryTime = hasDeliveryDetails(order) ? order.deliveryDetails?.time : null

    return (
        <Stack gap="0.25rem" className={classes.headerDropdownMenuItem}>
            <Typography color="textPrimary" className={classes.dropdownItemTitle}>
                {idAndName}
            </Typography>
            {deliveryTime && (
                <Typography variant="body2" color="textSecondary">
                    {dateTimeFactory.formatCustom(dateTimeFactory.fromApi(deliveryTime), "d. MMM yyyy, HH:mm")}
                </Typography>
            )}
            {personName && (
                <Typography variant="body2" color="textSecondary">
                    {personName}
                </Typography>
            )}
        </Stack>
    )
}

type SlipModalHeaderProps = {
    isAuthenticated: boolean
    isSelectMode: boolean
    orders: OrdersType
    orderDetails?: MeetingDetailsOrder
    handleOrderChange: (event: SelectChangeEvent<string>) => void
    orderIdsLength?: number
    uid?: string
    id?: number
    variant?: "slip" | "receipt" | "pos"
}

export const ReceiptAndSlipDialogHeader = ({
    isAuthenticated,
    isSelectMode,
    orders,
    orderDetails,
    handleOrderChange,
    variant = "receipt",
    orderIdsLength,
    uid,
    id,
}: SlipModalHeaderProps) => {
    const { classes } = useStyles()
    const selectedOrder = orders?.find((order) => order.uid === uid || order.id === id)
    const [searchQuery, setSearchQuery] = useState("")

    const filteredOrders = useMemo(
        () =>
            orders.filter((order) => {
                const searchLower = searchQuery?.toLowerCase() || ""
                if (!searchLower) return true

                const organizers = hasOrganizers(order) ? order.organizers : []
                const orderId = order.id?.toString() || ""
                const meetingName = order.meetingDetails?.meetingName?.toLowerCase() || ""
                const organizerNames = hasOrganizers(order)
                    ? organizers?.map((org) => org.name?.toLowerCase() || "").join(" ")
                    : ""
                const customerName = hasCustomer(order) ? order.customer.displayName.toLowerCase() : ""

                return (
                    orderId.includes(searchLower) ||
                    meetingName.includes(searchLower) ||
                    organizerNames.includes(searchLower) ||
                    customerName.includes(searchLower)
                )
            }),
        [searchQuery, orders],
    )

    if (isSelectMode) {
        return (
            <LocalizedStrict
                id={
                    variant === "slip"
                        ? "production-package-slip-header-orders-count"
                        : variant === "receipt"
                        ? "user-order-receipt-page-header-orders-count"
                        : "user-pos-order-receipt-page-header-orders-count"
                }
                vars={{ orderCount: orderIdsLength || 0 }}
            >
                <Typography className={classes.headerText}>Packing slips: {orderIdsLength} selected</Typography>
            </LocalizedStrict>
        )
    }
    if (isAuthenticated) {
        return (
            <Select
                className={classes.orderSelect}
                value={selectedOrder?.uid}
                onChange={handleOrderChange}
                renderValue={(value) => {
                    if (variant === "pos") {
                        return <SelectDisplayPosReceiptComponent value={value} orders={orders as AnyMeeting[]} />
                    }
                    if (variant === "receipt") {
                        return <SelectDisplayReceiptComponent value={value} meetings={orders as AnyMeeting[]} />
                    }
                    return <SelectDisplaySlipComponent value={value} orders={orders as AnyMeetingServiceOrder[]} />
                }}
                displayEmpty={false}
                variant="outlined"
                MenuProps={{
                    PaperProps: {
                        className: classes.orderSelectItem,
                    },
                }}
            >
                <SearchOrdersInPopup searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                {filteredOrders.map((order) => (
                    <MenuItem divider key={order.id} value={order.id} className={classes.menuItem}>
                        <ReceiptDisplayDropdownMenu order={order} />
                    </MenuItem>
                ))}
            </Select>
        )
    }

    if (orderDetails)
        return (
            <Typography className={classes.headerText}>
                {orderDetails?.meetingDetails?.meetingName}, #{orderDetails?.id}
            </Typography>
        )

    return <Box className={classes.layoutBlankFill} />
}
